import {
  InputType,
  ItemStatus,
  QuantityType,
  ActionSlot,
  ActionVariant,
  ControlKey,
  LegacyControlKey,
  MomentActionKey,
} from '@/constants'
import { isItemSelectionInput } from '@/utils/session'
import { isInteractivePreviewMode } from '@/utils/previewMode'
import { applyItemFilters, getItemIntents, isRecurring } from '@utils'
import { getIntentLabel } from '@utils/intents'
import type { MomentCardAction, MomentCardData, NextOrderDetailsData } from '@/types'
import { getButtonPlacement } from './themeTokens'
import { momentActionLabel } from './momentCard'

// Buy Again needs the campaign setting plus the item's own server-side capability.
export function canBuyAgain(item: any, settings: any): boolean {
  return item?.can_buy_again !== false && item?.can_charge_customer !== false && !!settings?.allow_buy_again
}

// A failed rebill that a new card can retry.
const PAYMENT_ISSUE_STATUSES: number[] = [
  ItemStatus.RecycleBillingFailed,
  ItemStatus.RecycleBilling,
  ItemStatus.RecycleFailedHold,
]

export function hasFailedRebill(item: any): boolean {
  return PAYMENT_ISSUE_STATUSES.includes(Number(item?.status))
}

// Only the latest cycle is ever rebilled — older cycles are left alone.
export function isLatestCycle(item: any): boolean {
  return Number(item?.billing_cycle) === Number(item?.latest_billing_cycle)
}

export function isSupersededCycle(item: any): boolean {
  const cycle = Number(item?.billing_cycle)
  const latest = Number(item?.latest_billing_cycle)
  return Number.isFinite(cycle) && Number.isFinite(latest) && cycle < latest
}

// The session step's allow-list. A list step that carries no intents supports no actions,
// so a button whose intent is missing would dead-end in startAction's pre-check.
export function hasSessionIntent(session: any, intent: number): boolean {
  return Array.isArray(session?.intents) && session.intents.includes(intent)
}

// Per-item view of that allow-list: a brand single-step path's filters can exclude an intent the
// session as a whole supports.
export function hasItemIntent(item: any, session: any, intent: number): boolean {
  return hasSessionIntent(session, intent) && applyItemFilters(item, [intent]).length > 0
}

function resolveItemIntents(item: any, session: any, campaign: any): number[] {
  if (!item) {
    return []
  }
  const settings = (campaign?.portal_settings as any) || {}
  const allowed = new Set<number>(Array.isArray(session?.intents) ? session.intents : [])
  return getItemIntents(item, settings, false).filter(intent => allowed.has(intent))
}

export type { ActionSlot, ActionVariant }

export interface ItemActionButton {
  key: string
  label: string
  intent: number | null
  slot: ActionSlot
  variant: ActionVariant
  icon?: string
}

export interface ControlButton {
  key: ControlKey
  label: string
  intents: number[]
  placeable?: boolean
}

const PRIMARY_CONTROLS: ControlKey[] = [ControlKey.SendNow, ControlKey.Reactivate]

export const CONTROL_BUTTONS: ControlButton[] = [
  { key: ControlKey.SendNow, label: 'Send Now', intents: [InputType.BillNow] },
  { key: ControlKey.ChangeVariant, label: 'Change Variant', intents: [InputType.ChangeVariant] },
  { key: ControlKey.RevisePackage, label: 'Revise Package', intents: [InputType.ChangeProduct] },
  { key: ControlKey.SwapProduct, label: 'Swap Product', intents: [InputType.ChangeSubscriptionProduct] },
  { key: ControlKey.Skip, label: 'Skip', intents: [InputType.SkipSubscription] },
  { key: ControlKey.Pause, label: 'Delay', intents: [InputType.PauseSubscription], placeable: true },
  { key: ControlKey.Reschedule, label: 'Reschedule', intents: [InputType.ChangeInterval] },
  { key: ControlKey.Reactivate, label: 'Reactivate & Save', intents: [InputType.ReactivateSubscription] },
  { key: ControlKey.TrackPackage, label: 'Track Package', intents: [InputType.ShipmentTracking] },
  {
    key: ControlKey.ReturnProduct,
    label: 'Return Product',
    intents: [InputType.Return, InputType.ReturnSubscription, InputType.ReturnTrial],
    placeable: true,
  },
  {
    key: ControlKey.CancelShipment,
    label: 'Cancel Shipment',
    intents: [InputType.Cancel, InputType.CancelSubscriptionHold, InputType.CancelRebillHold],
    placeable: true,
  },
  {
    key: ControlKey.CancelSubscription,
    label: 'Cancel Subscription',
    intents: [InputType.CancelSubscription, InputType.CancelTrial, InputType.CancelTrialHold],
    placeable: true,
  },
  { key: ControlKey.BuyAgain, label: 'Buy Again', intents: [] },
]

const SHOW_PREFIX = 'show_'
const LABEL_SUFFIX = '_label'
const PLACEMENT_SUFFIX = '_placement'

export function controlToggleField(key: ControlKey | LegacyControlKey): string {
  return `${SHOW_PREFIX}${key}`
}

export function controlLabelField(key: ControlKey | LegacyControlKey): string {
  return `${key}${LABEL_SUFFIX}`
}

function controlPlacementField(key: ControlKey): string {
  return `${key}${PLACEMENT_SUFFIX}`
}

function controlOn(config: NextOrderDetailsData | undefined, control: ControlButton): boolean {
  return (config as any)?.[controlToggleField(control.key)] === true
}

const LEGACY_ONLY_TOGGLES: string[] = Object.values(LegacyControlKey).map(controlToggleField)

export function namesControls(config: NextOrderDetailsData | undefined): boolean {
  if (!config) {
    return false
  }
  if (LEGACY_ONLY_TOGGLES.some(field => field in (config as any))) {
    return false
  }
  return CONTROL_BUTTONS.some(control => controlToggleField(control.key) in (config as any))
}

function backendLabel(item: any, intent: number | null | undefined): string {
  if (intent == null) {
    return ''
  }
  const label = getIntentLabel(item, intent)
  return label === String(intent) ? '' : label
}

function controlLabel(
  config: NextOrderDetailsData | undefined,
  control: ControlButton,
  item: any,
  intent: number | null
): string {
  const configured = (config as any)?.[controlLabelField(control.key)] as string
  return configured || backendLabel(item, intent) || control.label
}

function controlSlot(config: NextOrderDetailsData | undefined, control: ControlButton): ActionSlot {
  if (!control.placeable) {
    return ActionSlot.OrderControls
  }
  const configured = (config as any)?.[controlPlacementField(control.key)]
  return configured === ActionSlot.OrderControls ? ActionSlot.OrderControls : ActionSlot.PageBottom
}

const CANCEL_INTENTS = [
  InputType.Cancel,
  InputType.CancelSubscription,
  InputType.CancelSubscriptionHold,
  InputType.CancelRebillHold,
  InputType.CancelTrial,
  InputType.CancelTrialHold,
]
const RETURN_INTENTS = [InputType.Return, InputType.ReturnSubscription, InputType.ReturnTrial]

function placementFamily(intent: number): 'cancel' | 'return' | 'pause' | null {
  if (CANCEL_INTENTS.includes(intent)) {
    return 'cancel'
  }
  if (RETURN_INTENTS.includes(intent)) {
    return 'return'
  }
  if (intent === InputType.PauseSubscription) {
    return 'pause'
  }
  return null
}

// The two swaps share one `swap_label` but are different actions. 1005 (this order) keeps
// the merchant's label; 1027 (future cycles) takes a fixed one so they aren't identical.
const TOGGLE_MAP: Record<number, { key: ControlKey | LegacyControlKey; label?: string }> = {
  [InputType.ChangeInterval]: { key: ControlKey.Reschedule },
  [InputType.SkipSubscription]: { key: ControlKey.Skip },
  [InputType.ChangeProduct]: { key: LegacyControlKey.Swap },
  [InputType.ChangeSubscriptionProduct]: { key: LegacyControlKey.Swap, label: 'Swap Subscription Product' },
  [InputType.PauseSubscription]: { key: LegacyControlKey.Delay },
}

// A moment config is an allow-list: a key it doesn't mention is off. Only the
// absence of the whole config (order_details) means "show every available action".
function toggleOn(config: NextOrderDetailsData | undefined, key: ControlKey | LegacyControlKey): boolean {
  return config ? !!(config as any)[controlToggleField(key)] : true
}

// `strict` = a Home moment card: render only what the config names, nothing else.
// Detail pages instead show every available action, with the config's five switches
// (send_now / reschedule / skip / delay / swap) able to turn theirs off.
interface IntentContext {
  item: any
  campaign: any
  config: NextOrderDetailsData | undefined
  strict: boolean
  wantsReactivate: boolean
  hasPaymentUpdate: boolean
}

// Send Now bills the next recurring charge early — meaningless on a one-time order, and
// can_bill_now goes false once it has been used for this cycle.
function showsSendNow(item: any, config: NextOrderDetailsData | undefined, sessionIntents: Set<number>): boolean {
  const allowed = (config ? config.show_send_now === true : true) && isRecurring(item) && item?.can_bill_now !== false
  return allowed && sessionIntents.has(InputType.BillNow)
}

// The config toggle only governs the Home moment card. Everywhere else the button shows
// whenever this item's latest cycle has a failed rebill to retry.
function showsPaymentUpdate(
  item: any,
  config: NextOrderDetailsData | undefined,
  sessionIntents: Set<number>,
  strict: boolean
): boolean {
  if (strict) {
    return !!config?.show_resume_subscription && sessionIntents.has(InputType.RebillItem)
  }
  return hasFailedRebill(item) && isLatestCycle(item)
}

// Tracking is excluded from resolveItemIntents (it would double up with the button pushed
// here), so the session allow-list is applied directly. Unlike Vue's OrderTile — which only
// expands an inline panel — our button launches the step, so a missing intent is a dead end.
function showsTrackOrder(item: any, session: any, settings: any, config: NextOrderDetailsData | undefined): boolean {
  return (
    (config ? !!config.show_track_order : true) &&
    settings.show_tracking_in_list !== false &&
    item?.can_track_shipment !== false &&
    hasItemIntent(item, session, InputType.ShipmentTracking)
  )
}

// Buy Again runs the checkout flow (no intent); gate on settings like the list card.
function showsBuyAgain(item: any, settings: any, config: NextOrderDetailsData | undefined): boolean {
  return canBuyAgain(item, settings) && (config ? !!config.show_buy_again : !!settings.allow_buy_again)
}

function intentButton(intent: number, ctx: IntentContext): ItemActionButton | null {
  const { item, campaign, config, strict, wantsReactivate, hasPaymentUpdate } = ctx
  if (intent === InputType.ReactivateSubscription && wantsReactivate) {
    return null
  }

  const family = placementFamily(intent)
  if (family && !(family === 'pause' && config?.show_delay)) {
    if (strict) {
      return null
    }
    const slot = getButtonPlacement(campaign, family)
    return {
      key: `intent-${intent}`,
      label: getIntentLabel(item, intent),
      intent,
      slot,
      variant: ActionVariant.Secondary,
    }
  }

  const gate = TOGGLE_MAP[intent]
  if (gate) {
    if (!toggleOn(config, gate.key)) {
      return null
    }
    return {
      key: `intent-${intent}`,
      label: gate.label || ((config as any)?.[controlLabelField(gate.key)] as string) || getIntentLabel(item, intent),
      intent,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Secondary,
    }
  }

  if (strict) {
    return null
  }
  const isReactivate = intent === InputType.ReactivateSubscription
  // resume_subscription_label belongs to the payment-update button.
  const reactivateLabel = hasPaymentUpdate
    ? config?.reactivate_label
    : config?.resume_subscription_label || config?.reactivate_label
  return {
    key: `intent-${intent}`,
    label: isReactivate ? reactivateLabel || getIntentLabel(item, intent) : getIntentLabel(item, intent),
    intent,
    slot: ActionSlot.OrderControls,
    variant: isReactivate ? ActionVariant.Primary : ActionVariant.Secondary,
  }
}

// Underlining is a page-bottom treatment only, and only below the first button there — the
// bottom stack reads as one action with quieter alternates under it.
function applyPlacementVariants(buttons: ItemActionButton[]): ItemActionButton[] {
  let isFirstAtBottom = true
  return buttons.map(button => {
    if (button.slot !== ActionSlot.PageBottom) {
      return button.variant === ActionVariant.Tertiary ? { ...button, variant: ActionVariant.Secondary } : button
    }
    if (isFirstAtBottom) {
      isFirstAtBottom = false
      return button.variant === ActionVariant.Tertiary ? { ...button, variant: ActionVariant.Secondary } : button
    }
    return { ...button, variant: ActionVariant.Tertiary }
  })
}

function controlAvailable(
  control: ControlButton,
  item: any,
  session: any,
  campaign: any,
  sessionIntents: Set<number>,
  available: number[]
): number | null | undefined {
  const settings = (campaign?.portal_settings as any) || {}
  if (control.key === ControlKey.SendNow) {
    const allowed = isRecurring(item) && item?.can_bill_now !== false && sessionIntents.has(InputType.BillNow)
    return allowed ? InputType.BillNow : undefined
  }
  if (control.key === ControlKey.BuyAgain) {
    return canBuyAgain(item, settings) ? null : undefined
  }
  if (control.key === ControlKey.TrackPackage) {
    const allowed =
      settings.show_tracking_in_details !== false &&
      item?.can_track_shipment !== false &&
      hasItemIntent(item, session, InputType.ShipmentTracking)
    return allowed ? InputType.ShipmentTracking : undefined
  }
  return control.intents.find(intent => available.includes(intent))
}

function orderPageBottom(buttons: ItemActionButton[], config: NextOrderDetailsData | undefined): ItemActionButton[] {
  const saved = (config as any)?.page_bottom_order
  if (!Array.isArray(saved) || !saved.length) {
    return buttons
  }
  const rank = (button: ItemActionButton) => {
    const at = saved.indexOf(button.key)
    return at < 0 ? saved.length : at
  }
  const bottom = buttons.filter(b => b.slot === ActionSlot.PageBottom)
  const sorted = [...bottom].sort((a, b) => rank(a) - rank(b))
  let next = 0
  return buttons.map(button => (button.slot === ActionSlot.PageBottom ? sorted[next++] : button))
}

function controlOrder(config: NextOrderDetailsData | undefined): ControlButton[] {
  const saved = Object.keys(config ?? {})
    .filter(key => key.startsWith(SHOW_PREFIX))
    .map(key => key.slice(SHOW_PREFIX.length))
  return saved
    .map(key => CONTROL_BUTTONS.find(control => control.key === key))
    .filter((control): control is ControlButton => !!control)
}

export function buildControlButtons(
  item: any,
  session: any,
  campaign: any,
  config: NextOrderDetailsData | undefined
): ItemActionButton[] {
  if (!item || !config) {
    return []
  }
  const sessionIntents = new Set<number>(applyItemFilters(item, Array.isArray(session?.intents) ? session.intents : []))
  const available = resolveItemIntents(item, session, campaign)
  const preview = isInteractivePreviewMode()
  const buttons: ItemActionButton[] = []

  controlOrder(config).forEach(control => {
    if (!controlOn(config, control)) {
      return
    }
    const intent = preview
      ? (control.intents[0] ?? null)
      : controlAvailable(control, item, session, campaign, sessionIntents, available)
    if (intent === undefined) {
      return
    }
    buttons.push({
      key: control.key,
      label: controlLabel(config, control, item, intent),
      intent,
      slot: controlSlot(config, control),
      variant: PRIMARY_CONTROLS.includes(control.key) ? ActionVariant.Primary : ActionVariant.Secondary,
    })
  })

  return applyPlacementVariants(orderPageBottom(buttons, config))
}

export function buildItemActionButtons(
  item: any,
  session: any,
  campaign: any,
  config: NextOrderDetailsData | undefined,
  strict = false
): ItemActionButton[] {
  if (!item) {
    return []
  }
  // filtered per item so buttons gated on it directly (Send Now, Payment Update) respect the filters
  const sessionIntents = new Set<number>(applyItemFilters(item, Array.isArray(session?.intents) ? session.intents : []))
  const settings = (campaign?.portal_settings as any) || {}
  const buttons: ItemActionButton[] = []

  if (showsSendNow(item, config, sessionIntents)) {
    buttons.push({
      key: ControlKey.SendNow,
      label: config?.send_now_label || 'Send Now',
      intent: InputType.BillNow,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Primary,
    })
  }

  const hasPaymentUpdate = showsPaymentUpdate(item, config, sessionIntents, strict)
  if (hasPaymentUpdate) {
    buttons.push({
      key: 'payment_update',
      label: config?.resume_subscription_label || 'Resume Subscription',
      intent: InputType.RebillItem,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Primary,
    })
  }

  const wantsReactivate = !!config?.show_reactivate
  if (wantsReactivate && sessionIntents.has(InputType.ReactivateSubscription)) {
    buttons.push({
      key: ControlKey.Reactivate,
      label: config?.reactivate_label || getIntentLabel(item, InputType.ReactivateSubscription),
      intent: InputType.ReactivateSubscription,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Primary,
    })
  }

  const ctx: IntentContext = { item, campaign, config, strict, wantsReactivate, hasPaymentUpdate }
  for (const intent of resolveItemIntents(item, session, campaign)) {
    const button = intentButton(intent, ctx)
    if (button) {
      buttons.push(button)
    }
  }

  if (showsTrackOrder(item, session, settings, config)) {
    buttons.push({
      key: 'track_order',
      label: config?.track_order_label || 'Track Package',
      intent: InputType.ShipmentTracking,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Secondary,
    })
  }

  if (showsBuyAgain(item, settings, config)) {
    buttons.push({
      key: ControlKey.BuyAgain,
      label: config?.buy_again_label || 'Buy Again',
      intent: null,
      slot: ActionSlot.OrderControls,
      variant: ActionVariant.Secondary,
    })
  }

  return applyPlacementVariants(buttons)
}

function momentActionIntents(
  action: MomentCardAction,
  item: any,
  session: any,
  campaign: any,
  sessionIntents: Set<number>
): (number | null)[] {
  const settings = (campaign?.portal_settings as any) || {}
  const available = () => resolveItemIntents(item, session, campaign)
  switch (action.key) {
    case MomentActionKey.SendNow:
      return showsSendNow(item, undefined, sessionIntents) ? [InputType.BillNow] : []
    case MomentActionKey.RebillItem:
      return sessionIntents.has(InputType.RebillItem) ? [InputType.RebillItem] : []
    case MomentActionKey.Reactivate:
      return sessionIntents.has(InputType.ReactivateSubscription) ? [InputType.ReactivateSubscription] : []
    case MomentActionKey.Reschedule:
      return available().filter(intent => intent === InputType.ChangeInterval)
    case MomentActionKey.Skip:
      return available().filter(intent => intent === InputType.SkipSubscription)
    case MomentActionKey.Swap: {
      const swap = isRecurring(item) ? InputType.ChangeSubscriptionProduct : InputType.ChangeProduct
      return available().includes(swap) ? [swap] : []
    }
    case MomentActionKey.TrackOrder:
      return showsTrackOrder(item, session, settings, undefined) ? [InputType.ShipmentTracking] : []
    case MomentActionKey.BuyAgain:
      return canBuyAgain(item, settings) ? [null] : []
    default:
      return []
  }
}

export function buildMomentCardButtons(
  item: any,
  session: any,
  campaign: any,
  config: MomentCardData | undefined
): ItemActionButton[] {
  if (!item || !Array.isArray(config?.actions)) {
    return []
  }
  const sessionIntents = new Set<number>(applyItemFilters(item, Array.isArray(session?.intents) ? session.intents : []))
  const buttons: ItemActionButton[] = []

  config.actions.forEach(action => {
    if (!action || action.is_visible === false) {
      return
    }
    momentActionIntents(action, item, session, campaign, sessionIntents).forEach(intent => {
      buttons.push({
        key: action.key,
        label: momentActionLabel(action),
        intent,
        slot: ActionSlot.OrderControls,
        variant: buttons.length ? ActionVariant.Secondary : ActionVariant.Primary,
        icon: action.icon || undefined,
      })
    })
  })

  return buttons
}

export type ActionStepKind =
  | 'variants'
  | 'rmaItems'
  | 'paymentUpdate'
  | 'tracking'
  | 'itemStatus'
  | 'inputActionStatus'
  | 'exchangeProduct'
  | 'step'
  | 'done'

// A step whose first input field is a Product picker → swap/revise exchange-product selection.
export function exchangeProductInfo(session: any): { subscriptionOnly: boolean; quantityType: number } | null {
  for (const input of (session?.valid_inputs as any[]) || []) {
    const fields = input?.input_fields || {}
    const firstKey = Object.keys(fields)[0]
    const field = firstKey ? fields[firstKey] : null
    if (field && Number(field.type) === InputType.Product) {
      return { subscriptionOnly: !!field.subscription_only, quantityType: field.quantity_type ?? QuantityType.Any }
    }
  }
  return null
}

const CP_ITEM_SELECTION_TYPE = 1100

export function findItemSelectionInput(session: any): any | undefined {
  const pools = [session?.additional_inputs, session?.valid_inputs, session?.side_menu_inputs]
  for (const pool of pools) {
    if (Array.isArray(pool)) {
      const found = pool.find((i: any) => isItemSelectionInput(i) || Number(i?.type) === CP_ITEM_SELECTION_TYPE)
      if (found) {
        return found
      }
    }
  }
  return undefined
}

const PORTAL_STEP_TYPES = [
  InputType.CustomerPortalHome,
  InputType.CustomerPortalOrders,
  InputType.CustomerPortalSubscriptions,
  InputType.CustomerPortalSubscriptionDetail,
  InputType.CustomerPortalPersonalInfo,
  InputType.CustomerPortalOrderDetail,
]

// An items field of type RebillItem is the marker; the input's own type varies.
export function findRebillPaymentInput(session: any): any | undefined {
  const inputs = inputsOf(session)
  return (
    inputs.find((i: any) => Number(i?.input_fields?.items?.type) === InputType.RebillItem) ??
    inputs.find((i: any) => Number(i?.type) === InputType.PaymentInfo && !!i?.input_fields?.items)
  )
}

export function isPortalStep(session: any): boolean {
  return !!session && PORTAL_STEP_TYPES.includes(Number(session.step_type))
}

function inputsOf(session: any): any[] {
  return [...((session?.valid_inputs as any[]) || []), ...((session?.additional_inputs as any[]) || [])]
}

export function deriveActionStepKind(session: any, productChosen = false): ActionStepKind {
  if (!session) {
    return 'done'
  }
  if (PORTAL_STEP_TYPES.includes(Number(session.step_type))) {
    return 'done'
  }
  if (Number(session.step_type) === InputType.ShipmentTracking) {
    return 'tracking'
  }

  if (findRebillPaymentInput(session)) {
    return 'paymentUpdate'
  }

  const effectiveType = Number(session.step_type ?? (session.valid_inputs as any[])?.[0]?.type)
  if (effectiveType === InputType.ItemStatus) {
    return 'itemStatus'
  }
  if (effectiveType === InputType.InputActionStatus) {
    return 'inputActionStatus'
  }

  if (!productChosen && exchangeProductInfo(session)) {
    return 'exchangeProduct'
  }

  for (const input of inputsOf(session)) {
    const fields = input?.input_fields
    if (!fields) {
      continue
    }
    if (fields.variants) {
      return 'variants'
    }
    // The funnel only offers an item picker when the step has no item yet
    // (the funnel guards the whole branch on a selected item).
    if (!session.selected_item && Number(fields.items?.type) === InputType.ActiveItem) {
      return 'rmaItems'
    }
  }

  return 'step'
}

// False means the drawer has nothing left to show: a portal step came back, or the step has
// no inputs. It says nothing about whether the action succeeded.
export function hasRenderableStep(session: any): boolean {
  if (!session || isPortalStep(session)) {
    return false
  }
  return inputsOf(session).length > 0
}
